// Batch 0 (UX/IA overhaul) — one destructive-action confirmation.
//
// Fixes the audit's "confirmation asymmetry" P0: tenant-suspend and privilege
// escalation fire instantly today, while native window.confirm() is used
// elsewhere (Runbooks, Profiles, Alerts, Escalation). This replaces both — a
// consistent in-app dialog with an optional typed-confirmation guard for the
// highest-blast-radius actions (e.g. type the tenant name to suspend it), and
// a busy state so the action can't be double-fired.
import * as React from "react";
import { AlertTriangleIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** "destructive" (default) tints the confirm button red. */
  tone?: "destructive" | "default";
  /** When set, the confirm button stays disabled until the user types this
   *  exact string — for irreversible/high-impact actions. */
  requireText?: string;
  /** Shown as a spinner-y disabled state while the action runs. */
  busy?: boolean;
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "destructive",
  requireText,
  busy = false,
  onConfirm,
}: ConfirmDialogProps) {
  const [typed, setTyped] = React.useState("");

  // Reset the typed guard whenever the dialog re-opens.
  React.useEffect(() => {
    if (open) setTyped("");
  }, [open]);

  const gateOk = !requireText || typed === requireText;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {tone === "destructive" && (
              <AlertTriangleIcon className="h-5 w-5 text-destructive" />
            )}
            {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {requireText && (
          <div className="space-y-1.5">
            <Label htmlFor="confirm-guard" className="text-xs">
              Type <span className="font-mono font-semibold">{requireText}</span> to confirm
            </Label>
            <Input
              id="confirm-guard"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
            />
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={tone === "destructive" ? "destructive" : "default"}
            onClick={onConfirm}
            disabled={busy || !gateOk}
          >
            {busy ? "Working…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
